import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, catchError, filter, finalize, switchMap, take, tap, throwError } from 'rxjs';

import { AlertService } from './alert.service';
import { CORE_ETAG_HEADER, CORE_IF_MATCH_HEADER, CoreRecordPath, RecordRevisionStore } from './record-revision.store';

/** Stale validator — the record genuinely moved under the user. */
export const CORE_RECORD_CHANGED = 'CORE_RECORD_CHANGED';
/** Guarded write sent with no validator — our bug. */
export const CORE_REVISION_REQUIRED = 'CORE_REVISION_REQUIRED';
/** Malformed validator (`*`, weak, list, unquoted) — our bug. */
export const CORE_INVALID_REVISION_CONDITION = 'CORE_INVALID_REVISION_CONDITION';

/**
 * AK-720 recovery policy for AK-673 revision errors.
 *
 * Split from the interceptor so the policy (and, in the 412 case, the dialog) is testable and
 * editable without touching the HTTP plumbing.
 */
@Injectable({ providedIn: 'root' })
export class ConditionalWriteRecoveryService {
  private readonly store = inject(RecordRevisionStore);
  private readonly alertService = inject(AlertService);

  /**
   * 412 CORE_RECORD_CHANGED — a genuine user conflict, NOT a defect. The stale validator did its
   * job: someone else committed a newer revision. This must never auto-retry silently, because a
   * silent retry is precisely the lost update the validator exists to prevent — so the user is
   * asked, and only an explicit "Refresh" re-reads and resubmits their payload against the new
   * revision.
   *
   * Returns null when the error is not a 412, so the caller rethrows untouched.
   */
  recoverFromConflict(
    req: HttpRequest<unknown>,
    error: HttpErrorResponse,
    next: HttpHandlerFn,
    record: CoreRecordPath,
  ): Observable<HttpEvent<unknown>> | null {
    if (error.status !== 412) return null;

    // Deliberately do NOT clear the stale tag here. Keeping it means the NEXT guarded write
    // (e.g. if the user cancels and saves again) still carries that stale If-Match, the server
    // answers 412 again, and that routes straight back into this same recoverFromConflict path
    // — the user sees this same dialog again. Clearing it would instead send an UNGUARDED
    // write next time, which the server answers with 428 — and `recoverFromDefect` silently
    // self-heals a 428 with no dialog at all, which is exactly the lost update this validator
    // exists to prevent. The Refresh path doesn't need an explicit clear either, because
    // `rereadAndRetry` below never depends on the store for the retry — it uses the validator
    // it observed on the re-read itself, sending the retry unguarded when the re-read carried
    // none.
    const dialogRef = this.alertService.show(
      'Record changed',
      'This record was changed by someone else after you loaded it. Refresh to fetch the latest ' +
        'version and re-apply your change, or cancel to keep editing without saving.',
      ['Cancel', 'Refresh'],
    );

    return dialogRef.afterClosed().pipe(
      take(1),
      switchMap((choice: string | undefined) => {
        if (choice !== 'Refresh') return throwError(() => error);
        return this.rereadAndRetry(req, next, record, error);
      }),
      // Guards against an orphaned modal if the caller unsubscribes (e.g. component destroyed)
      // while the dialog is still open. Closing an already-closed dialog is a harmless no-op.
      finalize(() => dialogRef.close()),
    );
  }

  /**
   * Handles the revision errors that are FRONTEND DEFECTS rather than user conflicts:
   * 428 (guarded write with no validator) and 400 CORE_INVALID_REVISION_CONDITION (malformed
   * validator). Both are logged loudly and repaired the same way: drop whatever we held,
   * re-read the canonical record to capture the real validator, retry the write exactly once.
   *
   * Returns null when the error is not ours to handle, so the caller rethrows untouched.
   */
  recoverFromDefect(
    req: HttpRequest<unknown>,
    error: HttpErrorResponse,
    next: HttpHandlerFn,
    record: CoreRecordPath,
  ): Observable<HttpEvent<unknown>> | null {
    const code = this.store.extractCoreErrorCode(error);

    const isMissingRevision = error.status === 428;
    const isMalformedRevision = error.status === 400 && code === CORE_INVALID_REVISION_CONDITION;
    if (!isMissingRevision && !isMalformedRevision) return null;

    console.error(
      `[AK-720] ${code ?? error.status} on ${req.method} ${req.url} — this is a frontend ` +
        'conditional-write defect, not a user conflict. Recovering by re-reading the record.',
      error.error,
    );

    this.store.clearTag(record.recordKey);
    return this.rereadAndRetry(req, next, record, error);
  }

  /**
   * Re-read the canonical record, then retry the write once with whatever validator that read
   * yielded. Shared by the 428/400 defect path and the 412 refresh path.
   *
   * The re-read is the ORIGINAL request cloned into a GET, so it keeps the absolute URL and the
   * auth headers that upstream interceptors already attached — this interceptor runs last, so
   * `next` is the backend and nothing upstream would re-apply them. Its query params are cleared
   * too: a guarded write can carry params of its own (e.g. a filter/projection), and re-reading
   * with those params would risk hitting a filtered view of the record that carries no ETag at
   * all, rather than the canonical record.
   *
   * The retry is issued through `next` directly rather than through the interceptor chain, so
   * it cannot recurse: exactly one retry per failed write, always.
   */
  rereadAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    record: CoreRecordPath,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    const reread = req.clone({
      method: 'GET',
      body: null,
      params: new HttpParams(),
      headers: req.headers.delete(CORE_IF_MATCH_HEADER),
    });

    // Tracked locally rather than read back from the store: on the 412 conflict path the store
    // deliberately keeps holding the stale/rejected tag until a fresh one lands (see the comment
    // in `recoverFromConflict`), so `store.getTag` would still return that already-rejected
    // validator when the re-read comes back with no ETag of its own — guaranteeing a second,
    // identical failure. The re-read's own outcome is the only trustworthy source for the retry.
    let rereadTag: string | null = null;

    return next(reread).pipe(
      // The backend (real or `HttpTestingController`) emits an `HttpEventType.Sent` progress
      // event synchronously on subscribe, before the actual response. Filtering to
      // `HttpResponse` keeps `tap`/`switchMap` below from firing on that Sent event too — an
      // unfiltered `switchMap` would issue the retry TWICE for a single re-read.
      filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
      tap((event) => {
        rereadTag = event.headers.get(CORE_ETAG_HEADER);
        if (rereadTag) this.store.setTag(record.recordKey, rereadTag);
      }),
      // This `catchError` covers ONLY the re-read leg above (it sits before the `switchMap`
      // below, which is where the retry leg begins). A failed re-read means the caller's
      // action never got a fair retry, so masking it behind the original error is correct —
      // it is what the user's action actually hit.
      catchError((rereadError) =>
        throwError(() => (rereadError instanceof HttpErrorResponse ? originalError : rereadError)),
      ),
      switchMap(() => {
        // No fresh tag (record legitimately carries none yet) must NOT resend whatever
        // If-Match the interceptor's attach step baked into `req` — that header may be the
        // very stale/malformed validator that caused this recovery, and resending it verbatim
        // would repeat the identical failure. This is why `rereadTag` (observed straight off
        // the re-read above) drives the retry instead of the store: it is the only value
        // guaranteed to reflect what the re-read actually returned, on both the 412 and the
        // 428/400 paths.
        const retry = rereadTag
          ? req.clone({ headers: req.headers.set(CORE_IF_MATCH_HEADER, rereadTag) })
          : req.clone({ headers: req.headers.delete(CORE_IF_MATCH_HEADER) });
        // The retry is sent through `next` directly, so it bypasses the interceptor's own
        // `capture` tap (that tap only wraps the FIRST attempt). Without this, the store would
        // be left holding the re-read's tag instead of the retry write's authoritative one.
        // Its errors are deliberately left uncaught here: a retry-leg failure (e.g. a 412 from
        // a genuine concurrent conflict) must propagate AS ITSELF, not be masked as the
        // original 428/400 defect.
        return next(retry).pipe(
          tap((retryEvent) => {
            if (!(retryEvent instanceof HttpResponse)) return;
            if (retry.method.toUpperCase() === 'DELETE') {
              this.store.clearTag(record.recordKey);
              return;
            }
            const retryTag = retryEvent.headers.get(CORE_ETAG_HEADER);
            if (retryTag) this.store.setTag(record.recordKey, retryTag);
          }),
        );
      }),
    );
  }
}
